import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Card, Select, PageTitle, DemoBadge, StatusChip, Tag, Meta, Notice } from "../components/kit";
import { Loading, NoAccess } from "../components/common";
import { api, parseError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtDate } from "../lib/format";

const FIELDS = ["objective", "applicability", "exclusions", "practitioner_requirements", "preparation", "action_sequence", "product_functions", "progression_conditions", "alternatives", "stopping_criteria", "home_continuation", "outcome_observation", "sources"];

function MaterialLangSelect({ value, onChange }) {
  const { t } = useTranslation();
  return (
    <label className="flex items-center gap-2 text-sm font-semibold">{t("library.material_language")}
      <Select data-testid="material-language-select" className="w-auto" value={value} onChange={(e) => onChange(e.target.value)}><option value="en">English</option><option value="ru">Русский</option></Select>
    </label>
  );
}

function useMaterial() {
  const { me, setMe } = useAuth();
  const ml = me.user.material_language || "en";
  const set = (l) => api.put("/auth/profile", { material_language: l }).then((r) => setMe(r.data));
  return [ml, set];
}

export default function Library() {
  const { t } = useTranslation();
  const [ml, setMl] = useMaterial();
  const [f, setF] = useState({ direction: "", procedure: "", coat_type: "" });
  const [rows, setRows] = useState(null);
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/protocols", { params: { lang: ml, direction: f.direction || undefined, procedure: f.procedure || undefined, coat_type: f.coat_type || undefined } }).then((r) => setRows(r.data));
  }, [f, ml]);
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div>
      <PageTitle title={t("library.title")} sub={t("library.sub")} right={<MaterialLangSelect value={ml} onChange={setMl} />} />
      <Notice testid="library-demo-notice">{t("library.demo_notice")}</Notice>
      <Card className="my-5 grid gap-3 p-4 sm:grid-cols-4">
        <Select aria-label={t("practice.direction")} data-testid="library-direction-filter" value={f.direction} onChange={set("direction")}><option value="">{t("practice.all_directions")}</option>{["A", "B", "C", "D"].map((d) => <option key={d} value={d}>{d} — {t(`dir.${d}`)}</option>)}</Select>
        <Select aria-label={t("library.procedure")} data-testid="library-procedure-filter" value={f.procedure} onChange={set("procedure")}><option value="">{t("library.all_procedures")}</option>{["brushing", "bathing", "detangling", "handling", "drying"].map((p) => <option key={p} value={p}>{t(`procedure.${p}`)}</option>)}</Select>
        <Select aria-label={t("library.coat_type")} data-testid="library-coat-filter" value={f.coat_type} onChange={set("coat_type")}><option value="">{t("library.all_coats")}</option>{["any", "double", "short", "long", "curly"].map((c) => <option key={c} value={c}>{t(`coat.${c}`)}</option>)}</Select>
        <Select aria-label={t("library.status")} data-testid="library-status-filter" disabled value="demo"><option value="demo">{t("status.demo")}</option></Select>
      </Card>
      {!rows ? <Loading /> : (
        <ul className="grid gap-4 md:grid-cols-2" data-testid="protocol-list">
          {rows.map((p) => (
            <li key={p.id}>
              <Link to={`/library/${p.id}`} data-testid={`protocol-card-${p.number}`} className="lift block h-full rounded-2xl border bg-white p-5">
                <div className="flex flex-wrap items-center gap-2"><DemoBadge />{p.directions.map((d) => <Tag key={d}>{d}</Tag>)}<Tag>{t(`procedure.${p.procedure}`)}</Tag></div>
                {p.edition ? <h2 className="mt-3 font-display text-lg font-bold">{p.edition.title}</h2> : <p className="mt-3 font-semibold italic text-muted-foreground" data-testid="no-edition">{p.number} — {t("library.no_edition", { lang: t(`lang.${ml}`) })}</p>}
                {p.edition && <p className="mt-1 text-sm text-muted-foreground">{p.edition.summary}</p>}
                <dl className="mt-3 grid grid-cols-3 gap-2 text-sm">
                  <Meta label={t("library.author")}>{p.author}</Meta><Meta label={t("library.version")}>{p.current_version}</Meta><Meta label={t("library.review_date")}>{fmtDate(p.review_date)}</Meta>
                </dl>
              </Link>
            </li>
          ))}
        </ul>
      )}
      <h2 className="mb-3 mt-10 text-xl font-bold">{t("library.products")}</h2>
      <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid="product-catalogue">{products.map((p) => <li key={p.id} className="rounded-xl border bg-white p-3 text-sm"><b>{p.name}</b><div className="mt-1"><DemoBadge short /></div></li>)}</ul>
    </div>
  );
}

export function ProtocolDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [ml, setMl] = useMaterial();
  const [p, setP] = useState(null);
  const [denied, setDenied] = useState(false);
  const version = params.get("version");
  useEffect(() => {
    api.get(`/protocols/${id}`, { params: { lang: ml, version: version || undefined } }).then((r) => setP(r.data)).catch((e) => parseError(e).status === 404 && setDenied(true));
  }, [id, ml, version]);
  if (denied) return <NoAccess />;
  if (!p) return <Loading />;
  const e = p.edition;
  return (
    <div className="mx-auto max-w-3xl">
      <PageTitle title={e ? e.title : p.number} badge={<><DemoBadge />{p.directions.map((d) => <Tag key={d}>{d} — {t(`dir.${d}`)}</Tag>)}<StatusChip status="draft" label={t("status.demo")} /></>} right={<MaterialLangSelect value={ml} onChange={setMl} />} />
      <Card className="mb-4 grid gap-3 sm:grid-cols-4">
        <Meta label={t("library.author")}>{p.author}</Meta>
        <Meta label={t("library.version")} testid="protocol-version">{p.version}{p.version !== p.current_version && ` (${t("library.latest_is", { n: p.current_version })})`}</Meta>
        <Meta label={t("library.review_date")}>{fmtDate(p.review_date)}</Meta>
        <Meta label={t("library.languages")}>{p.languages.map((l) => t(`lang.${l}`)).join(", ")}</Meta>
        <div className="sm:col-span-4 flex flex-wrap gap-2">{p.versions.map((v) => <button key={v.version} data-testid={`protocol-version-${v.version}`} onClick={() => setParams({ version: v.version })} className={`min-h-[40px] rounded-full border px-3 text-sm font-semibold ${v.version === p.version ? "border-primary bg-[hsl(var(--peach))]" : "bg-white"}`}>{t("common.version_n", { n: v.version })}</button>)}</div>
      </Card>
      {!e ? <Notice tone="warn" testid="no-edition-notice">{t("library.no_edition", { lang: t(`lang.${ml}`) })}. {t("library.no_substitute")}</Notice> : (
        <div className="space-y-3" data-testid="protocol-fields">
          {!e.confirmed && <Notice tone="warn" testid="edition-unconfirmed">{t("library.unconfirmed")}</Notice>}
          {FIELDS.map((k) => <Card key={k} className="p-4"><h2 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">{t(`pfield.${k}`)}</h2><p className="mt-1" lang={ml}>{e[k] || "—"}</p></Card>)}
        </div>
      )}
    </div>
  );
}
